import React from "react";
import LinkButton from "../LinkButton";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import CartMenuButton from "../cart/CartMenuButton";
import AuthenticationMenu from "../AuthenticationMenu";
import {
  Button,
  Container,
  Grid,
  Hidden,
  Typography,
  AppBar,
  Toolbar,
} from "@material-ui/core";
import { grey } from "@material-ui/core/colors";
import clsx from "clsx";
import ElevationScroll from "./ElevationScroll";
import FadeScroll from "./FadeScroll";
import AppDrawer from "./AppDrawer";
import { useRouter } from "next/router";
import { useUser } from "src/context/AuthProvider";
import Topbar from "./Topbar";
import { FontWeight } from "theme/fonts";

const useBaseStyles = makeStyles(theme =>
  createStyles({
    toolbar: {
      display: "flex",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      // paddingTop: theme.spacing(1),
      // paddingBottom: theme.spacing(1),
      minHeight: 50,
      justifyContent: "space-between",
      padding: theme.spacing(0, 1),
    },
    cartButtonContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    viewCartButton: {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      margin: theme.spacing(1, 0),
      flex: "1 1",
    },
    nav: {
      flexGrow: 0,
      justifyContent: "flex-end",
      alignItems: "center",
      display: "flex",
      // maxWidth: 800
    },
    secondary: {
      padding: 0,
      minHeight: 100,
      backgroundColor: theme.palette.background.paper,
    },
    tertiary: {
      minHeight: 50,
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(0, 1),
    },
    offset: {
      ...theme.mixins.toolbar,
      height: 250,
      [theme.breakpoints.up("sm")]: {
        height: 170,
      },
      [theme.breakpoints.up("md")]: {
        height: 120,
      },
      [theme.breakpoints.up("lg")]: {
        height: 200,
      },
    },
    logo: {
      display: "flex",
      flexGrow: 1,
    },
    navLinkItem: {
      [theme.breakpoints.up("lg")]: {
        marginRight: theme.spacing(12),
      },
      [theme.breakpoints.up("md")]: {
        marginRight: theme.spacing(5),
        "&:last-child": {
          marginRight: theme.spacing(0),
        },
      },
      [theme.breakpoints.up("xs")]: {
        marginRight: theme.spacing(2),
      },
      lineHeight: 0.9,
      color: "inherit",
    },
    container: {
      display: "flex",
      alignItems: "center",
    },
    noLeft: {
      paddingLeft: 0,
    },
    noRight: {
      paddingRight: 0,
    },
    text: {
      fontSize: theme.typography.fontSize + 2,
    },
    pageTitle: {
      fontWeight: FontWeight.Bold,
      textAlign: "center",
    },
    pushRight: {
      justifyContent: "flex-end",
    },
    phoneNumber: {
      color: theme.palette.primary.main,
      fontWeight: "bold",
      fontSize: theme.typography.h6.fontSize,
      [theme.breakpoints.up("lg")]: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    companyName: {
      padding: theme.spacing(1),
      textAlign: "right",
      marginRight: theme.spacing(1),
      fontSize: theme.typography.fontSize + 5,
      color: grey[600],
    },
    boldText: {
      fontWeight: "bold",
      fontSize: theme.typography.h6.fontSize,
      [theme.breakpoints.up("lg")]: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    gold: {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.primary.main,
      margin: theme.spacing(2),
      "&:hover": {
        color: "white",
      },
    },
    leftAlign: {
      textAlign: "left",
    },
    rightAlign: {
      textAlign: "right",
    },
    phoneContainer: {
      justifyContent: "center",
    },
    chatContainer: {
      justifyContent: "center",
    },
  }),
);

export interface BaseHeaderProps {
  menuComponent?: React.ReactNode;
  title?: string;
}

export const BaseHeader = (_props: BaseHeaderProps) => {
  const classes = useBaseStyles();
  const { push } = useRouter();
  const { user } = useUser();

  return (
    <>
      <ElevationScroll>
        <AppBar position="fixed" color="transparent">
          <Topbar showCartButton />
          <Toolbar className={classes.toolbar} variant="dense">
            <Hidden smDown>
              <Container
                maxWidth="lg"
                className={clsx([classes.container, classes.pushRight])}
              >
                <nav className={classes.nav}>
                  <LinkButton className={classes.navLinkItem} href="/">
                    Home
                  </LinkButton>
                  <LinkButton className={classes.navLinkItem} href="/about">
                    About
                  </LinkButton>
                  <LinkButton className={classes.navLinkItem} href="/contact">
                    Contact
                  </LinkButton>
                  <LinkButton className={classes.navLinkItem} href="/services">
                    Services
                  </LinkButton>
                  <CartMenuButton className={classes.navLinkItem} />
                  {!user && (
                    <Button
                      className={classes.navLinkItem}
                      onClick={() =>
                        push({
                          pathname: "/api/login",
                          query: {
                            returnTo: "/register",
                            register: "true",
                          },
                        })
                      }
                    >
                      Register
                    </Button>
                  )}
                  <AuthenticationMenu className={classes.navLinkItem} />
                </nav>
              </Container>
            </Hidden>
            <Hidden mdUp>
              <AppDrawer />
              <CartMenuButton
                className={clsx([classes.navLinkItem, classes.pushRight])}
              />
            </Hidden>
          </Toolbar>
          <Toolbar className={classes.tertiary}>
            <Container maxWidth="lg">
              <Grid
                container
                justifyContent="space-between"
                className={clsx([classes.container, classes.pushRight])}
              >
                <Grid item xs={6}>
                  <FadeScroll threshold={55}>
                    <Typography
                      className={clsx([classes.pageTitle, classes.leftAlign])}
                    >
                      {_props.title}
                    </Typography>
                  </FadeScroll>
                </Grid>
                <Grid item xs={6}>
                  <Typography className={classes.rightAlign}>
                    Trace America LLC.
                  </Typography>
                </Grid>
              </Grid>
            </Container>
          </Toolbar>
        </AppBar>
      </ElevationScroll>
      <Toolbar className={classes.offset} />
    </>
  );
};

export default BaseHeader;
